import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BofuTabDocument,
  BofuTabFormsEmpresa,
  BofuTabItem,
  BofuTabStatus,
  DocumentResponse,
} from "@interfaces/response";
import { BOFU_TABS_CONFIG } from "@helpers/constantes";

export interface ExpedienteTabs {
  dictamen: BofuTabItem;
  empresa: BofuTabItem;
  garantia: BofuTabItem;
  personas: BofuTabItem;
}

export type ExpedienteEmpresaState = BofuTabItem & {
  doc_solicitud_credito: DocumentResponse | undefined;
  forms: BofuTabFormsEmpresa;
};

interface ExpedienteState {
  tabsReady: boolean;
  tabs: ExpedienteTabs;
  dictamen: BofuTabItem;
  empresa: ExpedienteEmpresaState;
}

const makeTab = (code: string, url: string, order: number): BofuTabItem => ({
  code: code as BofuTabItem["code"],
  url,
  order,
  is_active: false,
  is_disabled: true,
  tab_status: undefined,
  documents: [],
});

const initialState: ExpedienteState = {
  tabsReady: false,
  tabs: {
    dictamen: makeTab(BOFU_TABS_CONFIG.tab_dictamen.code, BOFU_TABS_CONFIG.tab_dictamen.url, 1),
    empresa: makeTab(BOFU_TABS_CONFIG.tab_empresa.code, BOFU_TABS_CONFIG.tab_empresa.url, 2),
    garantia: makeTab(BOFU_TABS_CONFIG.tab_garantia.code, BOFU_TABS_CONFIG.tab_garantia.url, 3),
    personas: makeTab(BOFU_TABS_CONFIG.tab_personas.code, BOFU_TABS_CONFIG.tab_personas.url, 4),
  },
  dictamen: makeTab(BOFU_TABS_CONFIG.tab_dictamen.code, BOFU_TABS_CONFIG.tab_dictamen.url, 1),
  empresa: {
    ...makeTab(BOFU_TABS_CONFIG.tab_empresa.code, BOFU_TABS_CONFIG.tab_empresa.url, 2),
    doc_solicitud_credito: undefined,
    forms: {
      solicitud_credito: { enabled: true, config: {} },
      referencias_personales: true,
      informacion_bancaria: true,
    },
  },
};

export const expedienteSlice = createSlice({
  name: "expediente",
  initialState,
  reducers: {
    setExpedienteTabs(state, action: PayloadAction<ExpedienteTabs>) {
      state.tabs = action.payload;
      state.tabsReady = true;
    },
    setExpedienteTabsReady(state, action: PayloadAction<boolean>) {
      state.tabsReady = action.payload;
    },
    setExpedienteDictamenDocument(
      state,
      action: PayloadAction<{ documentCode: string; value: BofuTabDocument }>,
    ) {
      const idx = state.tabs.dictamen.documents.findIndex((d) => d.file_category_code === action.payload.documentCode);
      if (idx !== -1) state.tabs.dictamen.documents[idx] = action.payload.value;
    },
    setExpedienteEmpresaDocument(
      state,
      action: PayloadAction<{ documentCode: string; value: BofuTabDocument }>,
    ) {
      const idx = state.tabs.empresa.documents.findIndex((d) => d.file_category_code === action.payload.documentCode);
      if (idx !== -1) state.tabs.empresa.documents[idx] = action.payload.value;
    },
    setExpedienteTabStatus(
      state,
      action: PayloadAction<{ tab: keyof ExpedienteTabs; status: BofuTabStatus | undefined }>,
    ) {
      state.tabs[action.payload.tab].tab_status = action.payload.status;
    },
    setExpedienteDictamen(state, action: PayloadAction<BofuTabItem>) {
      state.dictamen = action.payload;
    },
    setExpedienteEmpresa(state, action: PayloadAction<ExpedienteEmpresaState>) {
      state.empresa = action.payload;
    },
    setExpedienteDictamenDoc(
      state,
      action: PayloadAction<{
        document: BofuTabDocument;
        newValue: DocumentResponse | undefined;
        valueAction?: "add" | "remove" | "update" | null;
        valueIndex?: number;
      }>,
    ) {
      const { document, newValue, valueAction, valueIndex } = action.payload;
      const idx = state.dictamen.documents.findIndex(
        (doc) => doc.file_category_code === document.file_category_code && doc.relationship === document.relationship,
      );
      if (idx === -1) return;
      if (document.multiple) {
        if (valueAction === "add") state.dictamen.documents[idx].list_values.push(newValue);
        else if (valueAction === "update" && valueIndex !== undefined)
          state.dictamen.documents[idx].list_values[valueIndex] = newValue;
        else if (valueAction === "remove" && valueIndex !== undefined)
          state.dictamen.documents[idx].list_values.splice(valueIndex, 1);
      } else {
        state.dictamen.documents[idx].file_value = newValue;
      }
    },
    setExpedienteEmpresaDoc(
      state,
      action: PayloadAction<{
        document: BofuTabDocument;
        newValue: DocumentResponse | undefined;
        valueAction?: "add" | "remove" | "update" | null;
        valueIndex?: number;
      }>,
    ) {
      const { document, newValue, valueAction, valueIndex } = action.payload;
      const idx = state.empresa.documents.findIndex(
        (doc) => doc.file_category_code === document.file_category_code && doc.relationship === document.relationship,
      );
      if (idx === -1) return;
      if (document.multiple) {
        if (valueAction === "add") state.empresa.documents[idx].list_values.push(newValue);
        else if (valueAction === "update" && valueIndex !== undefined)
          state.empresa.documents[idx].list_values[valueIndex] = newValue;
        else if (valueAction === "remove" && valueIndex !== undefined)
          state.empresa.documents[idx].list_values.splice(valueIndex, 1);
      } else {
        state.empresa.documents[idx].file_value = newValue;
      }
    },
    resetExpediente() {
      return initialState;
    },
  },
});

export const {
  setExpedienteTabs,
  setExpedienteTabsReady,
  setExpedienteDictamenDocument,
  setExpedienteEmpresaDocument,
  setExpedienteTabStatus,
  setExpedienteDictamen,
  setExpedienteEmpresa,
  setExpedienteDictamenDoc,
  setExpedienteEmpresaDoc,
  resetExpediente,
} = expedienteSlice.actions;

export default expedienteSlice.reducer;
